import { Autocomplete, Chip, TextField, Typography } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import { useTranslation } from 'react-i18next';
import { triggers } from '@mytaverse-shared/constants/audioZoneTriggers';
import { formatSpotRegion } from '@mytaverse-shared/utils/assistantScript';
import {
  FieldLabel,
  FormField,
  HintIcon,
  InlineCode,
  TagHintBox,
} from '../styled';
import { getTagChipComponent } from '../utils';

interface TagsFieldProps {
  allTags: string[];
  value: string[];
  lockedTag?: string;
  tags?: string[];
  title?: string;
  hideArea?: boolean;
  touched?: boolean;
  error?: string;
  onChange: (newTags: string[]) => void;
  onBlur: () => void;
}

const TagsField = ({
  allTags,
  value,
  lockedTag,
  tags,
  title,
  hideArea,
  touched,
  error,
  onChange,
  onBlur,
}: TagsFieldProps) => {
  const { t } = useTranslation('common');

  return (
    <FormField sx={{ display: 'none' }}>
      <FieldLabel>{t('assistant.ragStorageDialog.tags')}</FieldLabel>
      <Autocomplete
        multiple
        freeSolo
        options={allTags}
        value={value}
        onChange={(_, newValue) => {
          const removedTags = value.filter(
            (tag) => !(newValue as string[]).includes(tag),
          );
          const filteredValue = [
            ...(newValue as string[]),
            ...removedTags.filter(
              (autoCompleteTag) => autoCompleteTag === lockedTag,
            ),
          ];
          onChange(filteredValue);
        }}
        renderTags={(renderValue: string[], getTagProps) =>
          renderValue.map((option, index) => {
            const { onDelete, key, ...tagProps } = getTagProps({ index });
            const TagChip = getTagChipComponent(option);
            const isStyled = TagChip !== Chip;

            return (
              <TagChip
                key={key}
                variant={isStyled ? 'filled' : 'outlined'}
                label={
                  triggers.find((x) => x.value === option)?.title ?? option
                }
                {...tagProps}
                onDelete={option === lockedTag ? undefined : onDelete}
                deleteIcon={
                  <CancelIcon
                    data-testid={`context-dialog-tag-delete-${option}`}
                  />
                }
              />
            );
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            fullWidth
            id="tags"
            name="tags"
            onBlur={onBlur}
            error={touched && Boolean(error)}
            helperText={touched && error}
            inputProps={{
              ...params.inputProps,
              'data-testid': 'context-dialog-tags-input',
            }}
          />
        )}
      />
      {(tags ?? value).some((tag) =>
        tag.toLowerCase().startsWith('areacontext:'),
      ) && (
        <TagHintBox>
          <HintIcon />
          <Typography variant="body2" color="text.secondary" lineHeight={1.6}>
            {t('assistant.ragStorageDialog.areaContextHint', { area: title })}
            <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
              {(tags ?? value).slice(2).map((tag) => (
                <li key={tag}>{formatSpotRegion(tag)}</li>
              ))}
            </ul>
          </Typography>
        </TagHintBox>
      )}
      {!hideArea && (
        <TagHintBox sx={{ display: 'none' }}>
          <HintIcon />
          <Typography variant="body2" color="text.secondary" lineHeight={1.6}>
            Use <InlineCode component="code">Common:name</InlineCode> tags to
            share context across areas. When this area&apos;s context has no
            answer, any linked <InlineCode component="code">Common:</InlineCode>{' '}
            context is used as a fallback.
          </Typography>
        </TagHintBox>
      )}
    </FormField>
  );
};

export default TagsField;
